import {
  AlertTriangle,
  CalendarDays,
  Clapperboard,
  Filter,
  Megaphone,
  PartyPopper,
  RadioTower,
  RefreshCw,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";
import { CanAccess, useGetIdentity, useLoading, useRefresh } from "ra-core";
import { useMemo, type ReactNode } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { formatPct as formatFunnelPct, ratePct } from "../funnel/funnelSummary";
import { formatCents, formatInt } from "../marketing/marketingMetrics";
import {
  BarColumns,
  CardLink,
  CardSkeleton,
  DeltaChip,
  DistributionRows,
  SectionCard,
  StarMeter,
  StatTile,
  Unavailable,
  type BarColumn,
} from "./DashboardPrimitives";
import {
  STARS,
  deltaTone,
  formatSignedPercent,
  formatSignedPoints,
  formatWeekRange,
  headlineFunnelStages,
  percentDelta,
  pointDelta,
  studioFilmsSublabel,
  type WeekWindows,
} from "./executiveMetrics";
import { SalesTiles } from "./SalesTiles";
import {
  useNewContacts,
  useThisWeekFunnel,
  useWeekWindows,
  useWeeklyBroadcasts,
  useWeeklyEchoes,
  useWeeklyMarketing,
} from "./useExecutiveDashboard";

const WEEKDAY_INITIALS = ["M", "T", "W", "T", "F", "S", "S"];
const BASELINE_HINT = "vs the same point last week";
const FAILED_ECHOES_HREF = `/echoflicks_echoes?filter=${encodeURIComponent(
  JSON.stringify({ status: "failed" }),
)}`;
const NEW_CONTACTS_HREF = "/contacts?sort=first_seen&order=DESC";
const ECHO_BOARD_HREF = "/echoflicks_echoes?view=board";

/**
 * The home page: what happened this week, against the same point last week.
 * Built for a phone as much as a desk — one column on small screens, every
 * headline tile deep-links into the page that explains it, and each card
 * loads and fails independently.
 */
export const ExecutiveDashboard = ({ className }: { className?: string }) => {
  const windows = useWeekWindows();
  const echoes = useWeeklyEchoes(windows);
  const contacts = useNewContacts(windows);

  const { thisWeek, lastWeekToDate } = echoes.comparison;
  const deliveredDelta = percentDelta(
    thisWeek.delivered,
    lastWeekToDate.delivered,
  );
  const ratingDelta = pointDelta(
    thisWeek.averageRating,
    lastWeekToDate.averageRating,
  );
  const contactsDelta =
    contacts.current != null && contacts.previous != null
      ? percentDelta(contacts.current, contacts.previous)
      : null;

  return (
    <div className={cn("space-y-6 pb-8", className)}>
      <DashboardHeading windows={windows} updatedAt={echoes.dataUpdatedAt} />

      <section
        aria-label="This week at a glance"
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
      >
        <StatTile
          label="Echoes this week"
          icon={<Clapperboard className="size-4" />}
          to="/echoflicks_echoes"
          isPending={echoes.isPending}
          error={echoes.error}
          value={formatInt(thisWeek.delivered)}
          delta={
            <DeltaChip
              label={formatSignedPercent(deliveredDelta)}
              tone={deltaTone(deliveredDelta)}
              hint={BASELINE_HINT}
            />
          }
          sublabel={`${formatInt(lastWeekToDate.delivered)} by this time last week · ${formatInt(thisWeek.failed)} failed${studioFilmsSublabel(echoes.studioFilms, formatInt)}`}
        />
        <StatTile
          label="Average rating"
          icon={<Star className="size-4" />}
          to={ECHO_BOARD_HREF}
          isPending={echoes.isPending}
          error={echoes.error}
          value={
            thisWeek.averageRating == null
              ? "—"
              : thisWeek.averageRating.toFixed(1)
          }
          delta={
            <DeltaChip
              label={formatSignedPoints(ratingDelta)}
              tone={deltaTone(ratingDelta)}
              hint={BASELINE_HINT}
            />
          }
          sublabel={
            thisWeek.rated === 0
              ? "No ratings yet this week"
              : `${formatInt(thisWeek.rated)} ratings · ${formatInt(thisWeek.unrated)} delivered echoes still unrated`
          }
        >
          <StarMeter rating={thisWeek.averageRating} />
        </StatTile>
        <StatTile
          label="New contacts"
          icon={<Users className="size-4" />}
          to={NEW_CONTACTS_HREF}
          isPending={contacts.isPending}
          error={contacts.error}
          value={contacts.current == null ? "—" : formatInt(contacts.current)}
          delta={
            <DeltaChip
              label={formatSignedPercent(contactsDelta)}
              tone={deltaTone(contactsDelta)}
              hint={BASELINE_HINT}
            />
          }
          sublabel={
            contacts.previous == null
              ? "First contact this week"
              : `${formatInt(contacts.previous)} by this time last week`
          }
        />
        <CanAccess
          resource="marketing_metrics"
          action="list"
          accessDenied={<AttentionTile echoes={echoes} />}
        >
          <FunnelConversionTile fallback={<AttentionTile echoes={echoes} />} />
        </CanAccess>
      </section>

      <CanAccess resource="marketing_metrics" action="list">
        <SalesTiles />
      </CanAccess>

      <section className="grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Echoes delivered per day"
          icon={<TrendingUp className="size-4" />}
          action={<CardLink to="/echoflicks_echoes">Open echoes</CardLink>}
          className="lg:col-span-2"
        >
          {echoes.isPending ? (
            <CardSkeleton rows={5} />
          ) : echoes.error ? (
            <Unavailable error={echoes.error} />
          ) : (
            <DailyEchoesChart
              windows={windows}
              daily={echoes.daily}
              thisWeekTotal={thisWeek.delivered}
              lastWeekTotal={echoes.daily
                .filter((bucket) => !bucket.isThisWeek)
                .reduce((sum, bucket) => sum + bucket.count, 0)}
            />
          )}
        </SectionCard>

        <SectionCard
          title="Ratings this week"
          icon={<Star className="size-4" />}
          action={<CardLink to={ECHO_BOARD_HREF}>Open board</CardLink>}
        >
          {echoes.isPending ? (
            <CardSkeleton rows={5} />
          ) : echoes.error ? (
            <Unavailable error={echoes.error} />
          ) : thisWeek.rated === 0 ? (
            <p className="text-sm text-muted-foreground">
              No one has rated an echo yet this week.
            </p>
          ) : (
            <DistributionRows
              rows={STARS.map((star) => ({
                key: String(star),
                label: `${star} ★`,
                count: thisWeek.ratingDistribution[star],
                detail: `${Math.round((thisWeek.ratingDistribution[star] / thisWeek.rated) * 100)}%`,
                tone: star <= 3 ? "warning" : "default",
              }))}
            />
          )}
        </SectionCard>
      </section>

      <CanAccess resource="marketing_metrics" action="list">
        <GrowthSection windows={windows} />
      </CanAccess>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <SectionCard
          title="Top occasions"
          icon={<PartyPopper className="size-4" />}
          action={<CardLink to="/echoflicks_echoes">All echoes</CardLink>}
        >
          {echoes.isPending ? (
            <CardSkeleton />
          ) : echoes.error ? (
            <Unavailable error={echoes.error} />
          ) : echoes.occasions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No delivered echoes yet this week.
            </p>
          ) : (
            <DistributionRows
              rows={echoes.occasions.map((occasion) => ({
                key: occasion.label,
                label: occasion.label,
                count: occasion.count,
                detail: `${occasion.share}%`,
              }))}
            />
          )}
        </SectionCard>

        <BroadcastReachCard windows={windows} />

        <SectionCard
          title="Needs attention"
          icon={<AlertTriangle className="size-4" />}
          action={<CardLink to={ECHO_BOARD_HREF}>Open board</CardLink>}
        >
          {echoes.isPending ? (
            <CardSkeleton rows={3} />
          ) : echoes.error ? (
            <Unavailable error={echoes.error} />
          ) : (
            <AttentionList
              failed={thisWeek.failed}
              lowRated={thisWeek.lowRated}
              unrated={thisWeek.unrated}
            />
          )}
        </SectionCard>
      </section>
    </div>
  );
};

const DashboardHeading = ({
  windows,
  updatedAt,
}: {
  windows: WeekWindows;
  updatedAt: number;
}) => {
  const { identity } = useGetIdentity();
  const refresh = useRefresh();
  const loading = useLoading();
  const firstName = identity?.fullName?.trim().split(/\s+/)[0];
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  }, []);
  const updatedLabel = updatedAt
    ? new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(updatedAt)
    : null;

  return (
    <div className="flex flex-wrap items-end justify-between gap-3 pt-2">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {firstName ? `${greeting}, ${firstName}` : greeting}
        </h1>
        <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
          <CalendarDays className="size-4" aria-hidden="true" />
          <span>
            Week {windows.isoWeek.slice(-2)} · {formatWeekRange(windows)}
          </span>
          <span aria-hidden="true">·</span>
          <span>compared with the same point last week (UTC)</span>
        </p>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {updatedLabel ? <span>Updated {updatedLabel}</span> : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => refresh()}
          aria-label="Refresh dashboard"
        >
          <RefreshCw className={cn("size-4", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>
    </div>
  );
};

const DailyEchoesChart = ({
  windows,
  daily,
  thisWeekTotal,
  lastWeekTotal,
}: {
  windows: WeekWindows;
  daily: ReturnType<typeof useWeeklyEchoes>["daily"];
  thisWeekTotal: number;
  lastWeekTotal: number;
}) => {
  const columns: BarColumn[] = daily.map((bucket, index) => ({
    key: bucket.date,
    value: bucket.count,
    label: WEEKDAY_INITIALS[index % 7],
    title: `${bucket.date}: ${bucket.count} delivered`,
    emphasis: bucket.isToday
      ? "today"
      : bucket.isFuture
        ? "future"
        : bucket.isThisWeek
          ? "current"
          : "baseline",
  }));
  return (
    <div className="space-y-3">
      <BarColumns
        columns={columns}
        ariaLabel={`Echoes delivered per day: ${thisWeekTotal} so far this week, ${lastWeekTotal} last week`}
      />
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-primary" aria-hidden="true" />
          This week ({formatWeekRange(windows)}): {formatInt(thisWeekTotal)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="size-2.5 rounded-sm bg-muted-foreground/30"
            aria-hidden="true"
          />
          Last week: {formatInt(lastWeekTotal)}
        </span>
      </div>
    </div>
  );
};

/** Failed plus low-rated echoes this week: the tile everyone gets in slot four. */
const AttentionTile = ({
  echoes,
}: {
  echoes: ReturnType<typeof useWeeklyEchoes>;
}) => {
  const { thisWeek } = echoes.comparison;
  return (
    <StatTile
      label="Needs attention"
      icon={<AlertTriangle className="size-4" />}
      to={ECHO_BOARD_HREF}
      isPending={echoes.isPending}
      error={echoes.error}
      value={formatInt(thisWeek.lowRated + thisWeek.failed)}
      sublabel={`${formatInt(thisWeek.lowRated)} rated 1–3 stars · ${formatInt(thisWeek.failed)} failed`}
    />
  );
};

/** Admin tile: how much of this week's arrival cohort reached a rated echo. */
const FunnelConversionTile = ({ fallback }: { fallback: ReactNode }) => {
  const funnel = useThisWeekFunnel();
  const summary = funnel.summary;
  // Without the dashboard API (Supabase-only or demo backends) there is no
  // funnel aggregate, so the slot shows the attention tile instead of a blank.
  if (!funnel.available) return fallback;
  return (
    <StatTile
      label="Arrival → rated echo"
      icon={<Filter className="size-4" />}
      to="/funnel?view=summary"
      isPending={funnel.isPending}
      error={funnel.error}
      value={
        summary
          ? formatFunnelPct(summary.closed_conversion.arrival_to_rated_pct)
          : "—"
      }
      sublabel={
        summary
          ? `${formatInt(summary.cohort_size)} contacts arrived this week · ${formatInt(summary.echo_outcomes.delivered_echoes)} echoes delivered${summary.incomplete_cohort ? " · cohort still open" : ""}`
          : undefined
      }
    />
  );
};

/** Admin section: marketing spend/efficiency and the onboarding funnel. */
const GrowthSection = ({ windows }: { windows: WeekWindows }) => {
  const marketing = useWeeklyMarketing(windows);
  const funnel = useThisWeekFunnel();
  const week = marketing.comparison.thisWeek;
  const baseline = marketing.comparison.lastWeekToDate;
  const spendDelta =
    week.spend_cents != null && baseline.spend_cents != null
      ? percentDelta(week.spend_cents, baseline.spend_cents)
      : null;
  const stages = funnel.summary ? headlineFunnelStages(funnel.summary) : [];

  return (
    <section aria-label="Growth" className="grid gap-4 lg:grid-cols-2">
      <SectionCard
        title="Marketing this week"
        icon={<Megaphone className="size-4" />}
        action={<CardLink to="/marketing">Open marketing</CardLink>}
      >
        {marketing.isPending ? (
          <CardSkeleton rows={5} />
        ) : marketing.error ? (
          <Unavailable error={marketing.error} />
        ) : !marketing.hasRows ? (
          <p className="text-sm text-muted-foreground">
            The marketing rollup has no rows for this week yet. It runs once a
            day.
          </p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <Metric
              label="Ad spend"
              value={
                week.spend_cents == null
                  ? "Not connected"
                  : formatCents(week.spend_cents)
              }
              detail={
                week.spend_cents == null ? undefined : (
                  <DeltaChip
                    label={formatSignedPercent(spendDelta)}
                    tone="neutral"
                    hint="vs the same days last week"
                  />
                )
              }
            />
            <Metric
              label="Cost per echo"
              value={
                week.costPerEchoCents == null
                  ? "—"
                  : formatCents(week.costPerEchoCents)
              }
              detail={
                week.costPerEchoCents == null
                  ? "needs ad spend"
                  : `${formatInt(week.echoes_completed)} echoes attributed`
              }
            />
            <Metric
              label="Web visitors → WhatsApp"
              value={
                ratePct(week.wa_cta_clicks, week.web_visitors)
                  ? `${ratePct(week.wa_cta_clicks, week.web_visitors)}%`
                  : "—"
              }
              detail={`${formatInt(week.wa_cta_clicks)} clicks of ${formatInt(week.web_visitors)} visitors`}
            />
            <Metric
              label="Top source"
              value={week.topSource?.label ?? "—"}
              detail={
                week.topSource
                  ? `${formatInt(week.topSource.newContacts)} new contacts`
                  : "no attributed contacts yet"
              }
            />
          </dl>
        )}
        <p className="mt-4 text-xs text-muted-foreground">
          Daily rollup by first-contact date; excludes internal and test
          accounts, so it can trail the live tiles by up to a day.
        </p>
      </SectionCard>

      <SectionCard
        title="Onboarding funnel · this week's cohort"
        icon={<Filter className="size-4" />}
        action={<CardLink to="/funnel?view=summary">Open funnel</CardLink>}
      >
        {!funnel.available ? (
          <p className="text-sm text-muted-foreground">
            The funnel aggregate needs the dashboard API backend.
          </p>
        ) : funnel.isPending ? (
          <CardSkeleton rows={5} />
        ) : funnel.error || !funnel.summary ? (
          <Unavailable error={funnel.error} />
        ) : stages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No contacts have arrived in this cohort yet.
          </p>
        ) : (
          <DistributionRows
            rows={stages.map((stage) => ({
              key: stage.key,
              label: stage.label,
              count: stage.reached,
              detail: formatFunnelPct(stage.pct_of_cohort),
            }))}
          />
        )}
        {funnel.summary ? (
          <p className="mt-4 text-xs text-muted-foreground">
            {formatInt(funnel.summary.cohort_size)} contacts in the cohort ·{" "}
            {formatFunnelPct(
              funnel.summary.closed_conversion.arrival_to_rated_pct,
            )}{" "}
            reached a rated echo
            {funnel.summary.incomplete_cohort ? " · cohort still open" : ""}
          </p>
        ) : null}
      </SectionCard>
    </section>
  );
};

const Metric = ({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: React.ReactNode;
}) => (
  <div className="min-w-0">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd
      className="mt-0.5 truncate text-lg font-semibold tabular-nums"
      title={value}
    >
      {value}
    </dd>
    {detail ? (
      <dd className="mt-0.5 text-xs text-muted-foreground">{detail}</dd>
    ) : null}
  </div>
);

const BroadcastReachCard = ({ windows }: { windows: WeekWindows }) => {
  const broadcasts = useWeeklyBroadcasts(windows);
  const summary = broadcasts.summary;
  return (
    <SectionCard
      title="Broadcasts this week"
      icon={<RadioTower className="size-4" />}
      action={<CardLink to="/whatsapp_broadcasts">Open broadcasts</CardLink>}
    >
      {broadcasts.isPending ? (
        <CardSkeleton rows={3} />
      ) : broadcasts.error ? (
        <Unavailable error={broadcasts.error} />
      ) : summary.count === 0 ? (
        <p className="text-sm text-muted-foreground">
          No broadcasts were created this week.
        </p>
      ) : (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
          <Metric
            label="Broadcasts"
            value={formatInt(summary.count)}
            detail={`${formatInt(summary.recipients)} recipients`}
          />
          <Metric
            label="Delivered"
            value={formatInt(summary.delivered)}
            detail={
              summary.recipients
                ? `${ratePct(summary.delivered, summary.recipients)}% of recipients`
                : undefined
            }
          />
          <Metric
            label="Read"
            value={formatInt(summary.read)}
            detail={
              summary.delivered
                ? `${ratePct(summary.read, summary.delivered)}% of delivered`
                : undefined
            }
          />
          <Metric
            label="Echoes started"
            value={formatInt(summary.echoesStarted)}
            detail="from broadcast replies"
          />
        </dl>
      )}
    </SectionCard>
  );
};

const AttentionList = ({
  failed,
  lowRated,
  unrated,
}: {
  failed: number;
  lowRated: number;
  unrated: number;
}) => {
  const rows = [
    {
      key: "failed",
      label: "Failed echoes",
      count: failed,
      to: FAILED_ECHOES_HREF,
      tone: failed > 0 ? "warning" : "muted",
    },
    {
      key: "low",
      label: "Rated 1–3 stars",
      count: lowRated,
      to: ECHO_BOARD_HREF,
      tone: lowRated > 0 ? "warning" : "muted",
    },
    {
      key: "unrated",
      label: "Delivered, not yet rated",
      count: unrated,
      to: ECHO_BOARD_HREF,
      tone: "muted",
    },
  ] as const;
  return (
    <ul className="divide-y divide-border/60">
      {rows.map((row) => (
        <li key={row.key}>
          <CardLinkRow
            to={row.to}
            label={row.label}
            count={row.count}
            tone={row.tone}
          />
        </li>
      ))}
    </ul>
  );
};

const CardLinkRow = ({
  to,
  label,
  count,
  tone,
}: {
  to: string;
  label: string;
  count: number;
  tone: "warning" | "muted";
}) => (
  <Link
    to={to}
    className="flex items-center justify-between gap-3 py-2 text-sm hover:text-foreground"
  >
    <span className="text-muted-foreground">{label}</span>
    <span
      className={cn(
        "rounded-md px-2 py-0.5 font-semibold tabular-nums",
        tone === "warning"
          ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
          : "bg-muted text-foreground",
      )}
    >
      {formatInt(count)}
    </span>
  </Link>
);
